import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { FormattedMessage } from "react-intl"
import { WingsDetailText, WingsIcon, WingsLink } from "@wingspans-ui"
import { publicPageGroups } from "../home-page/publicPages"

/**
 * dropdown with menu of links on hover
 */
const PublicHeaderNavDropdown = ({ group }) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Closes the menu when focus or a click moves outside of it, or on Escape
  useEffect(() => {
    if (!isOpen) return undefined
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [isOpen])

  /**
   * A pointer click (detail > 0) never closes an open menu
   * @param {React.MouseEvent<HTMLButtonElement>} e
   */
  const onTriggerClick = (e) => {
    if (isOpen && e.detail > 0) return
    setIsOpen(!isOpen)
  }

  return (
    <div
      ref={containerRef}
      className={`public-header-nav-dropdown${isOpen ? " public-header-nav-dropdown--open" : ""}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="public-header-nav-dropdown-trigger"
        aria-expanded={isOpen}
        onClick={onTriggerClick}
      >
        <WingsDetailText>
          <FormattedMessage {...group.titleMessage} />
        </WingsDetailText>
        <WingsIcon icon="chevron-down-regular" size="tiny" className="public-header-nav-chevron" color="var(--w-color-gray-900)" />
      </button>
      <div className="public-header-nav-menu-positioner" inert={isOpen ? undefined : "true"}>
        <div className="public-header-nav-menu w-p-2 w-rounded w-flex w-flex-column w-gap-1">
          {group.pages.map((page) => (
            <WingsLink
              key={page.key}
              to={page.path}
              size="detail"
              className="public-header-nav-link"
              onClick={() => setIsOpen(false)}
            >
              <FormattedMessage {...page.titleMessage} />
            </WingsLink>
          ))}
        </div>
      </div>
    </div>
  )
}

PublicHeaderNavDropdown.propTypes = {
  group: PropTypes.object.isRequired,
}

/**
 * Centered links to the public pages. Groups with several pages are dropdowns.
 * A group with a single page is a plain link to that page.
 * @returns {JSX}
 */
const PublicHeaderNav = () => (
  <nav className="public-header-nav w-flex w-flex-row w-justify-center w-items-center w-gap-2">
    {publicPageGroups.map((group) => {
      if (group.pages.length === 1) {
        const [page] = group.pages
        return (
          <WingsLink key={page.key} to={page.path} size="detail" className="public-header-nav-link">
            <FormattedMessage {...page.titleMessage} />
          </WingsLink>
        )
      }
      return <PublicHeaderNavDropdown key={group.key} group={group} />
    })}
  </nav>
)

export default PublicHeaderNav
